import { Component, Input, Output, EventEmitter, forwardRef, CUSTOM_ELEMENTS_SCHEMA, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-sl-select',
  standalone: true,
  imports: [NgFor, NgIf],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="sl-select-wrapper">
      <label *ngIf="label" class="block text-sm font-medium text-gray-700 mb-2">
        {{ label }}
        <span *ngIf="required" class="text-red-500 ml-1">*</span>
      </label>
      
      <sl-select
        #selectElement
        [placeholder]="placeholder"
        [disabled]="disabled"
        [multiple]="multiple"
        [clearable]="clearable"
        [size]="size"
        (sl-change)="onSelectionChange($event)"
        (sl-blur)="onBlur()"
        [class.invalid]="isFieldInvalid()">
        <sl-option 
          *ngFor="let option of options" 
          [value]="sanitizeValue(option.value)"
          [disabled]="option.disabled">
          {{ option.label }}
        </sl-option>
      </sl-select>
      
      <div *ngIf="getValidationError()" class="mt-1 text-sm text-red-600">
        {{ getValidationError() }}
      </div>
    </div>
  `,
  styleUrls: ['./sl-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SlSelectComponent),
      multi: true
    }
  ]
})
export class SlSelectComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @ViewChild('selectElement', { static: false }) selectElement!: ElementRef;

  @Input() label: string = '';
  @Input() placeholder: string = 'Select an option';
  @Input() options: SelectOption[] = [];
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() multiple: boolean = false;
  @Input() clearable: boolean = true;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() invalid: boolean = false;
  @Input() helpText: string = '';
  @Input() formControl: any; // FormControl for validation
  @Input() fieldName: string = '';

  @Output() selectionChange = new EventEmitter<string | string[]>();

  private _value: string | string[] = '';
  private onChange = (value: string | string[]) => {};
  private onTouched = () => {};

  ngAfterViewInit(): void {
    // Set initial value after view init
    if (this._value && this.selectElement) {
      setTimeout(() => {
        this.selectElement.nativeElement.value = this.multiple ? this._value : this.sanitizeValue(this._value as string);
      });
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  // ControlValueAccessor implementation
  writeValue(value: string | string[]): void {
    this._value = value || (this.multiple ? [] : '');
    
    if (this.selectElement?.nativeElement) {
      // Handle the value setting for Shoelace select
      if (this.multiple) {
        this.selectElement.nativeElement.value = Array.isArray(value) ? value.map(v => this.sanitizeValue(v)) : [];
      } else {
        this.selectElement.nativeElement.value = value ? this.sanitizeValue(value as string) : '';
      }
    }
  }

  registerOnChange(fn: (value: string | string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectionChange(event: any): void {
    const selectedValue = event.target.value;
    
    // Convert sanitized values back to original values
    let actualValue: string | string[];
    
    if (this.multiple) {
      actualValue = Array.isArray(selectedValue) 
        ? selectedValue.map(v => this.desanitizeValue(v))
        : [];
    } else {
      actualValue = selectedValue ? this.desanitizeValue(selectedValue) : '';
    }
    
    this._value = actualValue;
    this.onChange(actualValue);
    this.selectionChange.emit(actualValue);
  }

  onBlur(): void {
    this.onTouched();

    // Mark field as touched for validation display
    if (this.formControl) {
      this.formControl.markAsTouched();
    }
  }

  // Sanitize values to remove spaces (Shoelace requirement)
  private sanitizeValue(value: string): string {
    if (!value) return '';
    return value.replace(/\s+/g, '_');
  }

  // Convert sanitized values back to original
  private desanitizeValue(value: string): string {
    if (!value) return '';
    
    // Find the original option that matches this sanitized value
    const option = this.options.find(opt => this.sanitizeValue(opt.value) === value);
    return option ? option.value : value.replace(/_/g, ' ');
  }

  // Helper method to get display value
  getDisplayValue(): string {
    if (this.multiple) {
      return Array.isArray(this._value) ? this._value.join(', ') : '';
    }
    return this._value as string || '';
  }

  // Validation methods
  getValidationError(): string {
    if (!this.formControl || !this.formControl.errors || !this.formControl.touched) {
      return '';
    }

    const errors = this.formControl.errors;

    if (errors['required']) {
      return `${this.getFieldDisplayName()} is required`;
    }

    // Generic error message
    return `Please select a valid ${this.getFieldDisplayName().toLowerCase()}`;
  }

  private getFieldDisplayName(): string {
    if (this.label) {
      return this.label.replace(' *', '');
    }

    if (this.fieldName) {
      // Convert camelCase to readable format
      return this.fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    }

    return 'Field';
  }

  isFieldInvalid(): boolean {
    return !!(this.formControl && this.formControl.invalid && this.formControl.touched);
  }
}
